import { useEffect, useMemo, useState } from 'react'
import styles from './style.module.scss'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import Joi from 'joi'
import clsx from 'clsx'
import { joiResolver } from '@hookform/resolvers/joi'
import { toast } from 'react-toastify'

import MiniHeader from '../../UI/MiniHeader'
import Btn from '../../UI/Btn'
import TabBar from '../../UI/TabBar'
import RecordComments from '../../UI/RecordComments'
import SelectField from '../../UI/SelectField/index'
import TextField from '../../UI/TextField/index'

import http from '../../shared/http'
import eventEmitter, { types } from '../../shared/eventEmitter'
import { TRoutes, TApi, StateOptions, SchoolStudentCountOptions, RECORD_TYPES } from '../../shared/const'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

function CreateSchool(props) {
	const { t } = useTranslation()
	const { id } = useParams()
	const location = useLocation()
	const path = location.pathname
	const isCreate = path === TRoutes.CREATE_SCHOOL

	// „Anzahl Schüler:innen" ist beim Anlegen Pflicht (Anforderung #9); beim
	// Bearbeiten optional, damit Bestandsschulen ohne Wert speicherbar bleiben.
	const validationSchema = useMemo(
		() =>
			Joi.object({
				schoolName: Joi.string().min(2).max(64).required(),
				schoolType: Joi.string().required(),
				schoolState: Joi.string().required(),
				street: Joi.string().required(),
				number: Joi.number().min(1).max(1000).required(),
				zipCode: Joi.number().min(1).required(),
				location: Joi.string().min(2).max(64).required(),
				studentCount: isCreate ? Joi.string().required() : Joi.string().allow('', null)
			}),
		[isCreate]
	)

	const {
		register,
		control,
		handleSubmit,
		setValue,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(validationSchema)
	})

	const history = useHistory()
	const [isLoading, setLoading] = useState(false)
	// Die Maske ist zugleich die Detailansicht einer Schule. Was in der Liste
	// im Drei-Punkte-Menü steht, gehört deshalb auch hierher — dafür sind der
	// Name und der Freigabestand nötig, die das Formular selbst nicht behält.
	const [school, setSchool] = useState(null)
	// Beim Anlegen gibt es noch keinen Datensatz — und damit nichts zu
	// kommentieren. Die Leiste erscheint erst an einer bestehenden Schule.
	const [tab, setTab] = useState('data')

	const onSubmit = data => {
		setLoading(true)

		const schema = {
			name: data.schoolName,
			type: data.schoolType,
			streetName: data.street,
			number: data.number,
			zipCode: data.zipCode,
			location: data.location,
			state: data.schoolState,
			studentCount: data.studentCount || null
		}

		const request =
			path === TRoutes.CREATE_SCHOOL
				? http.post(TApi.CREATE_SCHOOL, schema)
				: http.put(TApi.ADMIN_SCHOOL_ID(id), schema)

		request
			.then(() => {
				toast.success(
					path === TRoutes.CREATE_SCHOOL
						? t('School has been created successfully.')
						: t('School has been updated successfully.')
				)
				history.push(TRoutes.SCHOOLS)
			})
			.finally(() => setLoading(false))
	}

	const fetchSchoolData = () => {
		setLoading(true)
		http.get(TApi.ADMIN_SCHOOL_ID(id))
			.then(({ data }) => {
				setSchool(data)
				setValue('schoolName', data.name)
				setValue('schoolType', data.type)
				setValue('schoolState', data.state)
				setValue('street', data.streetName)
				setValue('number', data.number)
				setValue('zipCode', data.zipCode)
				setValue('location', data.location)
				setValue('studentCount', data.studentCount || '')
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => {
		path !== TRoutes.CREATE_SCHOOL && fetchSchoolData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const confirmSchool = () => {
		setLoading(true)
		http.put(TApi.ADMIN_SCHOOL_VERIFY(id), { verified: true })
			.then(() => {
				toast.success(t('The school has been approved'))
				setSchool(previous => ({ ...previous, verified: true }))
			})
			.finally(() => setLoading(false))
	}

	const mergeSchool = () => {
		// Nach dem Zusammenführen gibt es diese Schule nicht mehr — die
		// Detailansicht dazu wäre leer, deshalb zurück in die Liste.
		eventEmitter.emit(types.openMergeSchoolModal, school, () => history.push(TRoutes.SCHOOLS))
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.root}>
				<MiniHeader title={isCreate ? t('Create new school') : school?.name || t('School')} bg="#f8faff" />

				{!isCreate && (
					<TabBar
						className={styles.tabs}
						tabs={[
							{ key: 'data', label: 'Master data' },
							{ key: 'comments', label: 'Comments' }
						]}
						active={tab}
						onChange={setTab}
					/>
				)}

				{tab === 'comments' && (
					<div className={styles.section}>
						<div className={styles.sectionContent}>
							<RecordComments entityType={RECORD_TYPES.SCHOOL} entityId={id} />
						</div>
					</div>
				)}

				<form className={clsx(tab !== 'data' && styles.hidden)} onSubmit={handleSubmit(onSubmit)}>
					<div className={styles.section}>
						<div className={styles.sectionContent}>
							<div className={styles.flexContainer}>
								<SelectField
									required
									disabled={isLoading}
									mb={30}
									label={t('School Types')}
									control={control}
									name="schoolType"
									error={errors.schoolType}
									placeholder={t('Select')}
									options={props.schoolTypes?.map(v => ({
										value: v.value,
										label: v.value
									}))}
								/>

								<TextField
									required
									disabled={isLoading}
									mb={30}
									label={t('School Name')}
									placeholder={t('School Name')}
									error={errors.schoolName}
									register={register('schoolName')}
								/>
							</div>

							<div className={styles.flexContainer}>
								<TextField
									required
									disabled={isLoading}
									mb={30}
									label={t('Street')}
									placeholder={t('Street')}
									error={errors.street}
									register={register('street')}
								/>

								<TextField
									required
									disabled={isLoading}
									mb={30}
									label={t('Nummer')}
									placeholder={t('Nummer')}
									error={errors.number}
									register={register('number')}
								/>
							</div>
							<div className={styles.flexContainer}>
								<TextField
									required
									disabled={isLoading}
									mb={30}
									label={t('Zip Code')}
									placeholder={t('Zip Code')}
									error={errors.zipCode}
									register={register('zipCode')}
								/>

								<TextField
									required
									disabled={isLoading}
									mb={30}
									label={t('City')}
									placeholder={t('City')}
									error={errors.location}
									register={register('location')}
								/>
							</div>
							<SelectField
								required
								disabled={isLoading}
								mb={20}
								label={t('state')}
								control={control}
								name="schoolState"
								error={errors.schoolState}
								placeholder={t('Select')}
								options={StateOptions.map(v => ({
									label: v.label,
									value: v.value
								})).sort((a, b) => a.label.localeCompare(b.label))}
							/>
							<SelectField
								required={isCreate}
								disabled={isLoading}
								mb={20}
								label={t('studentCount')}
								control={control}
								name="studentCount"
								error={errors.studentCount}
								placeholder={t('Select')}
								options={SchoolStudentCountOptions}
							/>
						</div>
					</div>
					<div className={styles.btnCreate}>
						{/* Aus dem Drei-Punkte-Menü der Liste: Freigeben und
						    Zusammenführen gehören auch an die geöffnete Schule. */}
						{!isCreate && (
							<>
								<Btn
									type="button"
									theme="secondary"
									disabled={isLoading || !school || school.verified}
									onClick={confirmSchool}
								>
									{school?.verified ? t('Verified') : t('Approve school')}
								</Btn>
								<Btn
									type="button"
									theme="secondary"
									disabled={isLoading || !school}
									onClick={mergeSchool}
								>
									{t('Merge school')}
								</Btn>
							</>
						)}
						<Btn
							loading={isLoading}
							width={214}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</form>
			</div>
		</div>
	)
}
CreateSchool.propTypes = { schoolTypes: PropTypes.array.isRequired }
const mapStateToProps = state => ({
	schoolTypes: state.options.schoolTypes
})
export default connect(mapStateToProps)(CreateSchool)
